import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight, Check, Lock, Building2, MapPin, DollarSign, Calendar, Layers, Users, Package, UserPlus, Search, Trash2, Mail, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import api from '@/lib/api';

const STEPS = ['Basic Info', 'Client', 'Location', 'Financial', 'Schedule', 'Modules', 'Team', 'Review'];
const STEP_ICONS = [Package, Building2, MapPin, DollarSign, Calendar, Layers, Users, Check];

const PROJECT_TYPES = ['GREENFIELD','BROWNFIELD','EXPANSION','REVAMP','DEBOTTLENECKING','MAINTENANCE_TURNAROUND','LUMP_SUM_TURNKEY'];
const SECTORS = ['OIL_AND_GAS_UPSTREAM','OIL_AND_GAS_DOWNSTREAM','PETROCHEMICAL','LNG','REFINERY','FERTILIZER','POWER_THERMAL','POWER_SOLAR','POWER_WIND','METALS_AND_MINING','CEMENT','WATER_AND_WASTEWATER','INFRASTRUCTURE','DEFENSE','PHARMACEUTICAL','DATA_CENTER','MANUFACTURING'];
const EXEC_MODELS = ['EPC_TURNKEY','EPCM','PMC','OWNERS_TEAM','DESIGN_BUILD','CONSTRUCTION_ONLY','BOT','PPP'];
const CONTRACT_FORMS = ['FIDIC_RED','FIDIC_YELLOW','FIDIC_SILVER','NEC4','INDIAN_BESPOKE','LSTK','COST_PLUS','GMP'];
const SCALES = ['MEGA','LARGE','MEDIUM','SMALL'];
const PRIORITIES = ['CRITICAL','HIGH','MEDIUM','LOW'];
const DISCIPLINES = ['CIV','STR','PIP','MEC','ELE','INS','PROC','HVAC','TELECOM'];

export default function ProjectCreationWizard() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(false);
  const [allUsers, setAllUsers] = useState([]);
  const [userSearch, setUserSearch] = useState('');
  const [adminMode, setAdminMode] = useState('existing'); // 'existing' | 'new'
  const [newAdminForm, setNewAdminForm] = useState({ email: '', firstName: '', lastName: '', password: '', company: '' });
  const [form, setForm] = useState({
    projectCode: '', projectName: '', projectShortName: '', projectDescription: '',
    classification: { projectType: 'GREENFIELD', industrySector: 'INFRASTRUCTURE', executionModel: 'EPC_TURNKEY', contractForm: '', projectScale: 'LARGE', projectPriority: 'HIGH', disciplines: [], tags: [] },
    client: { clientName: '', clientType: '', clientContactPerson: '', clientContactEmail: '' },
    pmcConsultant: { consultantName: '' },
    epcContractor: { contractorName: '' },
    location: { country: 'India', state: '', city: '', gpsCoordinates: { latitude: null, longitude: null } },
    financial: { sanctionedCost: null, currency: 'INR' },
    schedule: { projectStartDate: '', projectEndDate: '', workingDaysPerWeek: 6, hoursPerDay: 10 },
    activatedModules: {},
    admins: [], // [{ userId?, email, firstName, lastName, company, password?, isNew, projectRole }]
    tagInput: '',
  });

  useEffect(() => {
    api.get('/modules').then(r => {
      const mods = r.data?.modules || [];
      setModules(mods);
      const activated = {};
      mods.forEach(m => { activated[m.moduleCode] = { enabled: m.defaultEnabled || m.isMandatory }; });
      setForm(f => ({ ...f, activatedModules: activated }));
    }).catch(() => {});
    api.get('/users').then(r => setAllUsers(r.data?.users || [])).catch(() => {});
  }, []);

  const updateField = (path, value) => {
    setForm(f => {
      const copy = { ...f };
      const parts = path.split('.');
      let ref = copy;
      for (let i = 0; i < parts.length - 1; i++) {
        ref[parts[i]] = { ...ref[parts[i]] };
        ref = ref[parts[i]];
      }
      ref[parts[parts.length - 1]] = value;
      return copy;
    });
  };

  const toggleDiscipline = (d) => {
    const discs = form.classification.disciplines || [];
    updateField('classification.disciplines', discs.includes(d) ? discs.filter(x => x !== d) : [...discs, d]);
  };

  const addTag = () => {
    if (form.tagInput.trim()) {
      updateField('classification.tags', [...(form.classification.tags || []), form.tagInput.trim()]);
      setForm(f => ({ ...f, tagInput: '' }));
    }
  };

  const toggleModule = (mc, mandatory) => {
    if (mandatory) return;
    setForm(f => ({ ...f, activatedModules: { ...f.activatedModules, [mc]: { enabled: !f.activatedModules[mc]?.enabled } } }));
  };

  const enabledCount = Object.values(form.activatedModules).filter(v => v.enabled).length;

  // Team step helpers
  const addExistingAdmin = (u) => {
    try {
      if (!u || !u.user_id) { toast.error('Invalid user'); return; }
      if (form.admins.find(a => a.userId === u.user_id)) {
        toast.error('User already added');
        return;
      }
      const email = u.email || '';
      const fullName = u.fullName || u.name || '';
      const firstName = u.firstName || fullName.split(' ')[0] || email || 'User';
      const lastName = u.lastName || fullName.split(' ').slice(1).join(' ') || '';
      setForm(f => ({ ...f, admins: [...f.admins, {
        userId: u.user_id, email, firstName, lastName,
        company: u.company || '', isNew: false, projectRole: 'PROJECT_DIRECTOR'
      }] }));
      setUserSearch('');
      toast.success(`${firstName} added as admin`);
    } catch (err) {
      console.error('addExistingAdmin failed', err);
      toast.error('Failed to add user');
    }
  };

  const addNewAdmin = () => {
    try {
      const { email, firstName, password } = newAdminForm;
      if (!email || !firstName || !password) {
        toast.error('Email, first name and password are required');
        return;
      }
      if (password.length < 6) { toast.error('Password must be at least 6 characters'); return; }
      if (form.admins.find(a => (a.email || '').toLowerCase() === email.toLowerCase())) {
        toast.error('An admin with this email has already been added');
        return;
      }
      setForm(f => ({ ...f, admins: [...f.admins, { ...newAdminForm, isNew: true, projectRole: 'PROJECT_DIRECTOR' }] }));
      setNewAdminForm({ email: '', firstName: '', lastName: '', password: '', company: '' });
      toast.success(`${firstName} queued for invitation`);
    } catch (err) {
      console.error('addNewAdmin failed', err);
      toast.error('Failed to queue admin');
    }
  };

  const removeAdmin = (idx) => {
    setForm(f => ({ ...f, admins: f.admins.filter((_, i) => i !== idx) }));
  };

  const updateAdminRole = (idx, role) => {
    setForm(f => ({ ...f, admins: f.admins.map((a, i) => i === idx ? { ...a, projectRole: role } : a) }));
  };

  const filteredUsers = (() => {
    if (!userSearch.trim()) return [];
    const q = userSearch.toLowerCase();
    const addedIds = new Set(form.admins.map(a => a.userId).filter(Boolean));
    return (allUsers || [])
      .filter(u => u && u.user_id && !addedIds.has(u.user_id))
      .filter(u => {
        const e = (u.email || '').toLowerCase();
        const fn = (u.fullName || u.name || '').toLowerCase();
        const f = (u.firstName || '').toLowerCase();
        const l = (u.lastName || '').toLowerCase();
        return e.includes(q) || fn.includes(q) || f.includes(q) || l.includes(q);
      })
      .slice(0, 8);
  })();

  const handleSubmit = async () => {
    if (!form.projectCode || !form.projectName) { toast.error('Project code and name are required'); return; }
    if (!form.admins || form.admins.length === 0) {
      toast.error('At least one project admin must be assigned in the Team step');
      setStep(6);
      return;
    }
    setLoading(true);
    try {
      const projectRes = await api.post('/projects', {
        projectCode: form.projectCode,
        projectName: form.projectName,
        projectShortName: form.projectShortName || form.projectCode,
        projectDescription: form.projectDescription,
        classification: form.classification,
        client: form.client,
        pmcConsultant: form.pmcConsultant,
        epcContractor: form.epcContractor,
        location: form.location,
        financial: form.financial,
        schedule: form.schedule,
        activatedModules: form.activatedModules,
      });
      const newProjectId = projectRes.data?.project_id;

      // Create new users and add all admins as members
      let addedCount = 0;
      let emailedCount = 0;
      for (const admin of form.admins) {
        try {
          let userId = admin.userId;
          if (admin.isNew) {
            const userRes = await api.post('/users', {
              email: admin.email, password: admin.password,
              firstName: admin.firstName, lastName: admin.lastName,
              company: admin.company, platformRole: 'USER'
            });
            userId = userRes.data?.user_id;
          }
          if (userId && newProjectId) {
            await api.post(`/projects/${newProjectId}/members`, {
              userId, projectRole: admin.projectRole || 'PROJECT_DIRECTOR'
            });
            addedCount += 1;
            // Send invitation email only to NEW admins (existing users already have credentials)
            if (admin.isNew) {
              try {
                const inviteRes = await api.post('/invitations/admin', {
                  email: admin.email,
                  firstName: admin.firstName,
                  tempPassword: admin.password,
                  projectId: newProjectId,
                  projectRole: admin.projectRole || 'PROJECT_DIRECTOR',
                });
                if (inviteRes.data?.status === 'ok') emailedCount += 1;
              } catch (mailErr) {
                console.error('Invite email failed for', admin.email, mailErr);
              }
            }
          }
        } catch (innerErr) {
          console.error('Failed to add admin', admin.email, innerErr);
          toast.error(`Failed to add ${admin.email}: ${innerErr.response?.data?.detail || 'error'}`);
        }
      }

      const emailMsg = emailedCount > 0 ? ` (${emailedCount} invite email${emailedCount !== 1 ? 's' : ''} sent)` : '';
      toast.success(`Project created with ${addedCount} admin${addedCount !== 1 ? 's' : ''}!${emailMsg}`);
      navigate('/project-selector');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create project');
    } finally { setLoading(false); }
  };

  const inp = "w-full px-3 py-2 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#1E4E8C]";
  const inpStyle = { backgroundColor: 'var(--surface-bg)', color: 'var(--text-primary)', border: '1px solid var(--border-color)' };
  const lbl = "block text-xs font-medium mb-1";
  const lblStyle = { color: 'var(--text-muted)' };

  const renderStep = () => {
    switch (step) {
      case 0: return (
        <div className="space-y-4" data-testid="wizard-step-basic">
          <div className="grid grid-cols-2 gap-4">
            <div><label className={lbl} style={lblStyle}>Project Code *</label><input className={inp} style={inpStyle} value={form.projectCode} onChange={e => updateField('projectCode', e.target.value)} placeholder="GR-REF-001" /></div>
            <div><label className={lbl} style={lblStyle}>Short Name</label><input className={inp} style={inpStyle} value={form.projectShortName} onChange={e => updateField('projectShortName', e.target.value)} placeholder="GR-REF" /></div>
          </div>
          <div><label className={lbl} style={lblStyle}>Project Name *</label><input className={inp} style={inpStyle} value={form.projectName} onChange={e => updateField('projectName', e.target.value)} placeholder="Greenfield Refinery — 15 MMTPA" /></div>
          <div><label className={lbl} style={lblStyle}>Description</label><textarea className={inp} style={inpStyle} rows={3} value={form.projectDescription} onChange={e => updateField('projectDescription', e.target.value)} /></div>
          <div className="grid grid-cols-2 gap-4">
            <div><label className={lbl} style={lblStyle}>Project Type</label><select className={inp} style={inpStyle} value={form.classification.projectType} onChange={e => updateField('classification.projectType', e.target.value)}>{PROJECT_TYPES.map(t => <option key={t} value={t}>{t.replace(/_/g, ' ')}</option>)}</select></div>
            <div><label className={lbl} style={lblStyle}>Industry Sector</label><select className={inp} style={inpStyle} value={form.classification.industrySector} onChange={e => updateField('classification.industrySector', e.target.value)}>{SECTORS.map(s => <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>)}</select></div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div><label className={lbl} style={lblStyle}>Execution Model</label><select className={inp} style={inpStyle} value={form.classification.executionModel} onChange={e => updateField('classification.executionModel', e.target.value)}>{EXEC_MODELS.map(m => <option key={m} value={m}>{m.replace(/_/g, ' ')}</option>)}</select></div>
            <div><label className={lbl} style={lblStyle}>Contract Form</label><select className={inp} style={inpStyle} value={form.classification.contractForm || ''} onChange={e => updateField('classification.contractForm', e.target.value)}><option value="">Select...</option>{CONTRACT_FORMS.map(c => <option key={c} value={c}>{c.replace(/_/g, ' ')}</option>)}</select></div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div><label className={lbl} style={lblStyle}>Project Scale</label><select className={inp} style={inpStyle} value={form.classification.projectScale} onChange={e => updateField('classification.projectScale', e.target.value)}>{SCALES.map(s => <option key={s} value={s}>{s}</option>)}</select></div>
            <div><label className={lbl} style={lblStyle}>Priority</label><select className={inp} style={inpStyle} value={form.classification.projectPriority} onChange={e => updateField('classification.projectPriority', e.target.value)}>{PRIORITIES.map(p => <option key={p} value={p}>{p}</option>)}</select></div>
          </div>
          <div>
            <label className={lbl} style={lblStyle}>Disciplines</label>
            <div className="flex flex-wrap gap-2">{DISCIPLINES.map(d => (<button key={d} type="button" onClick={() => toggleDiscipline(d)} className={`px-3 py-1 text-xs rounded-full border ${form.classification.disciplines?.includes(d) ? 'bg-[#1E4E8C] text-white border-[#1E4E8C]' : ''}`} style={form.classification.disciplines?.includes(d) ? {} : { borderColor: 'var(--border-color)', color: 'var(--text-muted)' }}>{d}</button>))}</div>
          </div>
          <div>
            <label className={lbl} style={lblStyle}>Tags</label>
            <div className="flex gap-2">
              <input className={inp + " flex-1"} style={inpStyle} value={form.tagInput} onChange={e => setForm(f => ({...f, tagInput: e.target.value}))} onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addTag())} placeholder="Add tag..." />
              <Button type="button" size="sm" onClick={addTag} className="bg-[#1E4E8C] text-white">Add</Button>
            </div>
            <div className="flex flex-wrap gap-1 mt-2">{form.classification.tags?.map((t,i) => <span key={i} className="px-2 py-0.5 text-xs rounded-full bg-[#1E4E8C]/20 text-[#1E4E8C]">{t} <button onClick={() => updateField('classification.tags', form.classification.tags.filter((_,j) => j !== i))}>&times;</button></span>)}</div>
          </div>
        </div>
      );
      case 1: return (
        <div className="space-y-4" data-testid="wizard-step-client">
          <div><label className={lbl} style={lblStyle}>Client Name</label><input className={inp} style={inpStyle} value={form.client.clientName} onChange={e => updateField('client.clientName', e.target.value)} placeholder="HPCL" /></div>
          <div><label className={lbl} style={lblStyle}>Client Type</label><select className={inp} style={inpStyle} value={form.client.clientType || ''} onChange={e => updateField('client.clientType', e.target.value)}><option value="">Select...</option><option value="PSU">PSU</option><option value="PRIVATE">Private</option><option value="GOVERNMENT">Government</option><option value="JV">Joint Venture</option><option value="MULTINATIONAL">Multinational</option></select></div>
          <div className="grid grid-cols-2 gap-4">
            <div><label className={lbl} style={lblStyle}>Contact Person</label><input className={inp} style={inpStyle} value={form.client.clientContactPerson || ''} onChange={e => updateField('client.clientContactPerson', e.target.value)} /></div>
            <div><label className={lbl} style={lblStyle}>Contact Email</label><input className={inp} style={inpStyle} value={form.client.clientContactEmail || ''} onChange={e => updateField('client.clientContactEmail', e.target.value)} /></div>
          </div>
          <hr style={{ borderColor: 'var(--border-color)' }} />
          <h3 className="text-sm font-semibold" style={{ color: 'var(--text-secondary)' }}>PMC / EPCM Consultant</h3>
          <div><label className={lbl} style={lblStyle}>Consultant Name</label><input className={inp} style={inpStyle} value={form.pmcConsultant.consultantName || ''} onChange={e => updateField('pmcConsultant.consultantName', e.target.value)} placeholder="Engineers India Limited" /></div>
          <hr style={{ borderColor: 'var(--border-color)' }} />
          <h3 className="text-sm font-semibold" style={{ color: 'var(--text-secondary)' }}>EPC Contractor</h3>
          <div><label className={lbl} style={lblStyle}>Contractor Name</label><input className={inp} style={inpStyle} value={form.epcContractor.contractorName || ''} onChange={e => updateField('epcContractor.contractorName', e.target.value)} placeholder="L&T Hydrocarbon Engineering" /></div>
        </div>
      );
      case 2: return (
        <div className="space-y-4" data-testid="wizard-step-location">
          <div className="grid grid-cols-2 gap-4">
            <div><label className={lbl} style={lblStyle}>Country</label><input className={inp} style={inpStyle} value={form.location.country} onChange={e => updateField('location.country', e.target.value)} /></div>
            <div><label className={lbl} style={lblStyle}>State</label><input className={inp} style={inpStyle} value={form.location.state} onChange={e => updateField('location.state', e.target.value)} /></div>
          </div>
          <div><label className={lbl} style={lblStyle}>City</label><input className={inp} style={inpStyle} value={form.location.city || ''} onChange={e => updateField('location.city', e.target.value)} /></div>
          <div className="grid grid-cols-2 gap-4">
            <div><label className={lbl} style={lblStyle}>Latitude</label><input type="number" step="0.001" className={inp} style={inpStyle} value={form.location.gpsCoordinates?.latitude || ''} onChange={e => updateField('location.gpsCoordinates.latitude', e.target.value ? parseFloat(e.target.value) : null)} /></div>
            <div><label className={lbl} style={lblStyle}>Longitude</label><input type="number" step="0.001" className={inp} style={inpStyle} value={form.location.gpsCoordinates?.longitude || ''} onChange={e => updateField('location.gpsCoordinates.longitude', e.target.value ? parseFloat(e.target.value) : null)} /></div>
          </div>
        </div>
      );
      case 3: return (
        <div className="space-y-4" data-testid="wizard-step-financial">
          <div className="grid grid-cols-2 gap-4">
            <div><label className={lbl} style={lblStyle}>Sanctioned Cost</label><input type="number" className={inp} style={inpStyle} value={form.financial.sanctionedCost || ''} onChange={e => updateField('financial.sanctionedCost', e.target.value ? parseFloat(e.target.value) : null)} placeholder="42000000000" /></div>
            <div><label className={lbl} style={lblStyle}>Currency</label><select className={inp} style={inpStyle} value={form.financial.currency} onChange={e => updateField('financial.currency', e.target.value)}><option value="INR">INR (₹)</option><option value="USD">USD ($)</option><option value="EUR">EUR</option><option value="AED">AED</option><option value="SAR">SAR</option></select></div>
          </div>
        </div>
      );
      case 4: return (
        <div className="space-y-4" data-testid="wizard-step-schedule">
          <div className="grid grid-cols-2 gap-4">
            <div><label className={lbl} style={lblStyle}>Start Date *</label><input type="date" className={inp} style={inpStyle} value={form.schedule.projectStartDate} onChange={e => updateField('schedule.projectStartDate', e.target.value)} /></div>
            <div><label className={lbl} style={lblStyle}>End Date *</label><input type="date" className={inp} style={inpStyle} value={form.schedule.projectEndDate} onChange={e => updateField('schedule.projectEndDate', e.target.value)} /></div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div><label className={lbl} style={lblStyle}>Working Days/Week</label><input type="number" className={inp} style={inpStyle} value={form.schedule.workingDaysPerWeek} onChange={e => updateField('schedule.workingDaysPerWeek', parseInt(e.target.value) || 6)} /></div>
            <div><label className={lbl} style={lblStyle}>Hours/Day</label><input type="number" className={inp} style={inpStyle} value={form.schedule.hoursPerDay} onChange={e => updateField('schedule.hoursPerDay', parseInt(e.target.value) || 10)} /></div>
          </div>
        </div>
      );
      case 5: {
        const grouped = {};
        modules.forEach(m => { const cat = m.moduleCategory || 'OTHER'; if (!grouped[cat]) grouped[cat] = []; grouped[cat].push(m); });
        return (
          <div className="space-y-4" data-testid="wizard-step-modules">
            <p className="text-xs" style={{ color: 'var(--text-muted)' }}>Select modules for this project. <Lock className="w-3 h-3 inline" /> = mandatory</p>
            <p className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>Activated: {enabledCount} of {modules.length}</p>
            <div className="max-h-[400px] overflow-y-auto space-y-4 pr-2">
              {Object.entries(grouped).map(([cat, mods]) => (
                <div key={cat}>
                  <h4 className="text-[10px] uppercase tracking-wider mb-2 font-semibold" style={{ color: 'var(--text-disabled)' }}>{cat.replace(/_/g, ' ')}</h4>
                  <div className="space-y-1">
                    {mods.map(m => {
                      const enabled = form.activatedModules[m.moduleCode]?.enabled;
                      return (
                        <div key={m.moduleCode} data-testid={`module-toggle-${m.moduleCode}`} onClick={() => toggleModule(m.moduleCode, m.isMandatory)}
                          className={`flex items-center gap-3 px-3 py-2 rounded-lg cursor-pointer transition-all ${enabled ? 'ring-1 ring-[#1E4E8C]' : ''}`}
                          style={{ backgroundColor: enabled ? 'rgba(30,78,140,0.1)' : 'var(--surface-bg)', border: '1px solid var(--border-color)' }}>
                          <div className={`w-5 h-5 rounded flex items-center justify-center text-xs ${enabled ? 'bg-[#1E4E8C] text-white' : ''}`} style={enabled ? {} : { border: '1px solid var(--border-color)' }}>
                            {enabled && <Check className="w-3 h-3" />}
                          </div>
                          <div className="flex-1 min-w-0">
                            <span className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>{m.moduleName}</span>
                          </div>
                          {m.isMandatory && <Lock className="w-3.5 h-3.5 shrink-0" style={{ color: 'var(--text-disabled)' }} />}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      }
      case 6: return (
        <div className="space-y-4" data-testid="wizard-step-team">
          <div className="rounded-lg p-3 text-xs" style={{ backgroundColor: 'rgba(30,78,140,0.1)', border: '1px solid rgba(30,78,140,0.3)', color: 'var(--text-secondary)' }}>
            <strong>Project Admins</strong> have full administrative control on this project, including user management, module activation and permission assignment.
            At least one admin is required. You (the project creator) will automatically be added as Project Director.
          </div>

          {/* Mode toggle */}
          <div className="flex gap-1 p-1 rounded-lg" style={{ backgroundColor: 'var(--surface-bg)' }}>
            {[['existing', 'Search Existing Users', Search], ['new', 'Invite New User', UserPlus]].map(([key, label, Icon]) => (
              <button key={key} type="button" onClick={() => setAdminMode(key)} data-testid={`admin-mode-${key}`}
                className="flex items-center gap-2 px-3 py-1.5 rounded-md text-xs font-medium flex-1 justify-center transition-colors"
                style={{ backgroundColor: adminMode === key ? 'var(--card-bg)' : 'transparent', color: adminMode === key ? 'var(--text-primary)' : 'var(--text-muted)' }}>
                <Icon className="w-3.5 h-3.5" /> {label}
              </button>
            ))}
          </div>

          {adminMode === 'existing' ? (
            <div>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: 'var(--text-disabled)' }} />
                <input className={inp + " pl-10"} style={inpStyle} placeholder="Search by name or email..."
                  value={userSearch} onChange={e => setUserSearch(e.target.value)} data-testid="admin-search-input" />
              </div>
              {userSearch.trim() && (
                <div className="mt-2 rounded-lg overflow-hidden" style={{ border: '1px solid var(--border-color)' }}>
                  {filteredUsers.length === 0 ? (
                    <div className="p-3 text-xs text-center" style={{ color: 'var(--text-muted)' }}>No users found</div>
                  ) : filteredUsers.map((u, idx) => (
                    <button key={u.user_id || `u-${idx}`} type="button" onClick={() => addExistingAdmin(u)} data-testid={`admin-user-${u.user_id}`}
                      className="w-full flex items-center gap-3 px-3 py-2 text-left transition-colors hover:bg-opacity-50"
                      style={{ backgroundColor: 'var(--surface-bg)', borderBottom: '1px solid var(--border-color)' }}>
                      <div className="w-8 h-8 rounded-full bg-[#1E4E8C] flex items-center justify-center text-white text-xs font-bold">
                        {((u.fullName || u.firstName || u.email || '?')[0] || '?').toUpperCase()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium truncate" style={{ color: 'var(--text-primary)' }}>{u.fullName || u.firstName || u.email || 'Unknown'}</div>
                        <div className="text-xs truncate" style={{ color: 'var(--text-muted)' }}>{u.email || ''}</div>
                      </div>
                      <Plus className="w-4 h-4" style={{ color: '#1E4E8C' }} />
                    </button>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-3 rounded-lg p-3" style={{ backgroundColor: 'var(--surface-bg)', border: '1px solid var(--border-color)' }}>
              <div className="grid grid-cols-2 gap-3">
                <div><label className={lbl} style={lblStyle}>First Name *</label><input className={inp} style={inpStyle} value={newAdminForm.firstName} onChange={e => setNewAdminForm(f => ({...f, firstName: e.target.value}))} placeholder="Rajesh" data-testid="new-admin-first" /></div>
                <div><label className={lbl} style={lblStyle}>Last Name</label><input className={inp} style={inpStyle} value={newAdminForm.lastName} onChange={e => setNewAdminForm(f => ({...f, lastName: e.target.value}))} placeholder="Kumar" /></div>
              </div>
              <div><label className={lbl} style={lblStyle}>Email *</label><input type="email" className={inp} style={inpStyle} value={newAdminForm.email} onChange={e => setNewAdminForm(f => ({...f, email: e.target.value}))} placeholder="rajesh@company.com" data-testid="new-admin-email" /></div>
              <div><label className={lbl} style={lblStyle}>Temporary Password *</label><input type="password" className={inp} style={inpStyle} value={newAdminForm.password} onChange={e => setNewAdminForm(f => ({...f, password: e.target.value}))} placeholder="Minimum 6 characters" data-testid="new-admin-password" /></div>
              <div><label className={lbl} style={lblStyle}>Company</label><input className={inp} style={inpStyle} value={newAdminForm.company} onChange={e => setNewAdminForm(f => ({...f, company: e.target.value}))} placeholder="Eleventerra Technologies" /></div>
              <Button type="button" onClick={addNewAdmin} className="w-full bg-[#0E7C6B] hover:bg-[#0a6357] text-white" data-testid="new-admin-add-btn">
                <UserPlus className="w-4 h-4 mr-2" /> Queue Invitation
              </Button>
            </div>
          )}

          {/* Assigned admins list */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className={lbl} style={lblStyle}>Assigned Admins ({form.admins.length})</label>
              {form.admins.length === 0 && <span className="text-xs" style={{ color: '#F59E0B' }}>At least 1 required</span>}
            </div>
            {form.admins.length === 0 ? (
              <div className="text-center py-6 rounded-lg text-xs" style={{ backgroundColor: 'var(--surface-bg)', border: '1px dashed var(--border-color)', color: 'var(--text-muted)' }}>
                No admins added yet. Search existing users or invite new ones above.
              </div>
            ) : (
              <div className="space-y-2" data-testid="admins-list">
                {form.admins.map((a, i) => (
                  <div key={i} data-testid={`admin-row-${i}`} className="flex items-center gap-3 px-3 py-2 rounded-lg"
                    style={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--border-color)' }}>
                    <div className="w-8 h-8 rounded-full bg-[#0E7C6B] flex items-center justify-center text-white text-xs font-bold shrink-0">
                      {(a.firstName || a.email || '?')[0]?.toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium truncate" style={{ color: 'var(--text-primary)' }}>{a.firstName} {a.lastName}</span>
                        {a.isNew && <span className="text-[9px] px-1.5 py-0.5 rounded-full uppercase" style={{ backgroundColor: 'rgba(14,124,107,0.2)', color: '#0E7C6B' }}>New</span>}
                      </div>
                      <div className="text-xs truncate flex items-center gap-1" style={{ color: 'var(--text-muted)' }}>
                        <Mail className="w-3 h-3" /> {a.email}
                      </div>
                    </div>
                    <select value={a.projectRole} onChange={e => updateAdminRole(i, e.target.value)} data-testid={`admin-role-${i}`}
                      className="text-xs px-2 py-1 rounded-md shrink-0"
                      style={{ backgroundColor: 'var(--surface-bg)', color: 'var(--text-primary)', border: '1px solid var(--border-color)' }}>
                      <option value="PROJECT_DIRECTOR">Project Director</option>
                      <option value="PROJECT_MANAGER">Project Manager</option>
                    </select>
                    <button type="button" onClick={() => removeAdmin(i)} data-testid={`admin-remove-${i}`} className="text-red-500 hover:text-red-600 shrink-0">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      );
      case 7: return (
        <div className="space-y-4" data-testid="wizard-step-review">
          <div className="text-center mb-4"><Check className="w-10 h-10 mx-auto text-[#0E7C6B]" /><h3 className="text-lg font-bold mt-2" style={{ color: 'var(--text-primary)' }}>Review & Create</h3></div>
          {[
            ['Project', `${form.projectCode} — ${form.projectName}`],
            ['Type', form.classification.projectType?.replace(/_/g, ' ')],
            ['Sector', form.classification.industrySector?.replace(/_/g, ' ')],
            ['Client', form.client.clientName || '—'],
            ['Location', `${form.location.city || ''} ${form.location.state || ''}, ${form.location.country}`],
            ['Budget', form.financial.sanctionedCost ? `${form.financial.currency} ${(form.financial.sanctionedCost / 1e9).toFixed(1)}B` : '—'],
            ['Duration', form.schedule.projectStartDate && form.schedule.projectEndDate ? `${form.schedule.projectStartDate} → ${form.schedule.projectEndDate}` : '—'],
            ['Modules', `${enabledCount} activated`],
            ['Admins', `${form.admins.length} assigned`],
          ].map(([k, v]) => (
            <div key={k} className="flex justify-between py-2" style={{ borderBottom: '1px solid var(--border-color)' }}>
              <span className="text-sm" style={{ color: 'var(--text-muted)' }}>{k}</span>
              <span className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>{v}</span>
            </div>
          ))}
        </div>
      );
      default: return null;
    }
  };

  const StepIcon = STEP_ICONS[step];
  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ backgroundColor: 'var(--app-bg)' }}>
      <div className="w-full max-w-2xl">
        <div className="flex items-center gap-1 mb-6 overflow-x-auto">
          {STEPS.map((s, i) => (
            <div key={i} className="flex items-center">
              <button onClick={() => i <= step && setStep(i)} className={`flex items-center gap-1 px-2 py-1 rounded text-[10px] font-medium transition-colors ${i === step ? 'bg-[#1E4E8C] text-white' : i < step ? 'text-[#1E4E8C]' : ''}`} style={i > step ? { color: 'var(--text-disabled)' } : {}} data-testid={`wizard-step-btn-${i}`}>
                {i < step ? <Check className="w-3 h-3" /> : null}{s}
              </button>
              {i < STEPS.length - 1 && <div className="w-4 h-px mx-1" style={{ backgroundColor: i < step ? '#1E4E8C' : 'var(--border-color)' }} />}
            </div>
          ))}
        </div>

        <div className="rounded-xl p-6" style={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--border-color)' }}>
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2 rounded-lg" style={{ backgroundColor: 'rgba(30,78,140,0.15)' }}><StepIcon className="w-5 h-5 text-[#1E4E8C]" /></div>
            <div>
              <h2 className="text-lg font-bold" style={{ color: 'var(--text-primary)' }}>Step {step + 1}: {STEPS[step]}</h2>
              <p className="text-xs" style={{ color: 'var(--text-muted)' }}>Fill in project details</p>
            </div>
          </div>

          <AnimatePresence mode="wait">
            <motion.div key={step} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.15 }}>
              {renderStep()}
            </motion.div>
          </AnimatePresence>

          <div className="flex justify-between mt-8">
            <Button variant="ghost" onClick={() => step === 0 ? navigate(-1) : setStep(s => s - 1)} style={{ color: 'var(--text-muted)' }}>
              <ChevronLeft className="w-4 h-4 mr-1" /> {step === 0 ? 'Cancel' : 'Back'}
            </Button>
            {step < STEPS.length - 1 ? (
              <Button onClick={() => {
                if (step === 6 && form.admins.length === 0) {
                  toast.error('Please assign at least one project admin before continuing');
                  return;
                }
                setStep(s => s + 1);
              }} className="bg-[#1E4E8C] hover:bg-[#143A6B] text-white" data-testid="wizard-next-btn">
                Next <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            ) : (
              <Button onClick={handleSubmit} disabled={loading} className="bg-[#0E7C6B] hover:bg-[#0a6357] text-white" data-testid="wizard-create-btn">
                {loading ? 'Creating...' : 'Create Project'}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
